import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MessageSquare, Loader2, Wand2, Copy, Check, FileText, Download, Sparkles, Mic, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PITCH_DURATIONS = [
  { value: "15s", label: "15 sec", words: "~40 words" },
  { value: "30s", label: "30 sec", words: "~75 words" },
  { value: "1min", label: "1 min", words: "~150 words" }
];

function MessagingContent({ user }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [hasDeck, setHasDeck] = useState(false);
  const [oneLiner, setOneLiner] = useState("");
  const [elevatorPitch, setElevatorPitch] = useState("");
  const [pitchDuration, setPitchDuration] = useState("1min");
  const [generatingOneLiner, setGeneratingOneLiner] = useState(false);
  const [generatingPitch, setGeneratingPitch] = useState(false);
  const [generatingDeck, setGeneratingDeck] = useState(false);
  const [copiedOneLiner, setCopiedOneLiner] = useState(false);
  const [copiedPitch, setCopiedPitch] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [profileRes, strategyRes, decksRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/company-profile`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/strategy`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/decks`, { credentials: "include" })
      ]);
      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfile(data.profile);
        if (data.profile?.one_liner) setOneLiner(data.profile.one_liner);
      }
      if (strategyRes.ok) {
        const data = await strategyRes.json();
        setStrategy(data.strategy);
      }
      if (decksRes.ok) {
        const data = await decksRes.json();
        setHasDeck(data.decks && data.decks.length > 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const generateOneLiner = async () => {
    setGeneratingOneLiner(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/messaging/one-liner`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setOneLiner(data.one_liner);
        toast.success("One-liner generated!");
      } else { toast.error("Failed to generate"); }
    } catch (e) { toast.error("Failed to generate"); }
    setGeneratingOneLiner(false);
  };

  const generateElevatorPitch = async () => {
    setGeneratingPitch(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/messaging/elevator-pitch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ duration: pitchDuration })
      });
      if (res.ok) {
        const data = await res.json();
        setElevatorPitch(data.elevator_pitch);
        toast.success("Elevator pitch generated!");
      } else { toast.error("Failed to generate"); }
    } catch (e) { toast.error("Failed to generate"); }
    setGeneratingPitch(false);
  };

  const generateRevisedDeck = async () => {
    if (!hasDeck) { toast.error("Upload a deck first"); return; }
    setGeneratingDeck(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/messaging/revised-deck`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${profile?.company_name || 'company'}_revised_deck.pptx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Deck downloaded!");
      } else { toast.error("Failed to generate deck"); }
    } catch (e) { toast.error("Failed to generate deck"); }
    setGeneratingDeck(false);
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'oneliner') { setCopiedOneLiner(true); setTimeout(() => setCopiedOneLiner(false), 2000); }
      else { setCopiedPitch(true); setTimeout(() => setCopiedPitch(false), 2000); }
      toast.success("Copied!");
    } catch (e) { toast.error("Failed to copy"); }
  };

  const saveOneLiner = async () => {
    if (!oneLiner) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/company-profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...profile, one_liner: oneLiner })
      });
      if (res.ok) toast.success("Saved to profile!");
      else toast.error("Failed to save");
    } catch (e) { toast.error("Failed to save"); }
  };

  const hasProfile = profile && profile.company_name && profile.archetype;
  const hasStrategy = strategy && Object.keys(strategy).length > 5;
  const readinessScore = [hasProfile, hasStrategy, hasDeck].filter(Boolean).length;

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Messaging</h1>
        <p className="text-slate-600 mt-1">Refine your company messaging based on your profile, strategy, and pitch deck</p>
      </div>

      <Card className={`border mb-6 ${readinessScore === 3 ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            {readinessScore === 3 ? <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" /> : <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />}
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-2">{readinessScore === 3 ? "Ready to Generate!" : "Complete Your Foundation First"}</h3>
              <div className="grid grid-cols-3 gap-3">
                {[{ ok: hasProfile, label: "Company Profile" }, { ok: hasStrategy, label: "Strategy Details" }, { ok: hasDeck, label: "Pitch Deck" }].map(i => (
                  <div key={i.label} className={`flex items-center gap-2 p-2 rounded ${i.ok ? 'bg-green-100' : 'bg-white'}`}>
                    {i.ok ? <CheckCircle className="w-4 h-4 text-green-600" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300" />}
                    <span className="text-sm">{i.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {/* One-Liner */}
        <Card className="border border-slate-200">
          <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" />Company One-Liner</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">Your company blurb is the front door of your fundraise. It's what gets forwarded, shared internally, and used to decide whether to meet you.</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Your One-Liner</Label>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(oneLiner, 'oneliner')} disabled={!oneLiner}>
                  {copiedOneLiner ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <Textarea value={oneLiner} onChange={(e) => setOneLiner(e.target.value)} placeholder="Your compelling one-liner will appear here..." rows={2} className="text-lg" />
              <p className="text-xs text-slate-500">{oneLiner.split(' ').filter(Boolean).length} words</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={generateOneLiner} disabled={generatingOneLiner} className="btn-secondary gap-2">
                {generatingOneLiner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {generatingOneLiner ? "Generating..." : "Generate One-Liner"}
              </Button>
              {oneLiner && oneLiner !== profile?.one_liner && (
                <Button variant="outline" onClick={saveOneLiner} className="gap-2"><CheckCircle className="w-4 h-4" />Save to Profile</Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Elevator Pitch */}
        <Card className="border border-slate-200">
          <CardHeader><CardTitle className="flex items-center gap-2"><Mic className="w-5 h-5 text-blue-500" />Elevator Pitch</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">A structured pitch covering: problem, solution, market, traction, and ask. Choose your duration based on the context.</p>
            
            <div className="space-y-2">
              <Label>Duration</Label>
              <ToggleGroup type="single" value={pitchDuration} onValueChange={(v) => v && setPitchDuration(v)} className="justify-start">
                {PITCH_DURATIONS.map(d => (
                  <ToggleGroupItem key={d.value} value={d.value} className="flex-col h-auto py-2 px-4 data-[state=on]:bg-teal-100 data-[state=on]:text-teal-700">
                    <span className="font-medium">{d.label}</span>
                    <span className="text-xs text-slate-500">{d.words}</span>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Your Elevator Pitch</Label>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(elevatorPitch, 'pitch')} disabled={!elevatorPitch}>
                  {copiedPitch ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <Textarea value={elevatorPitch} onChange={(e) => setElevatorPitch(e.target.value)} placeholder="Your elevator pitch will appear here..." rows={8} />
              {elevatorPitch && <p className="text-xs text-slate-500">{elevatorPitch.split(' ').filter(Boolean).length} words</p>}
            </div>
            
            <Button onClick={generateElevatorPitch} disabled={generatingPitch} className="btn-secondary gap-2">
              {generatingPitch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {generatingPitch ? "Generating..." : `Generate ${PITCH_DURATIONS.find(d => d.value === pitchDuration)?.label} Pitch`}
            </Button>
          </CardContent>
        </Card>

        {/* Revised Deck */}
        <Card className="border border-slate-200">
          <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-purple-500" />Revised Pitch Deck</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">Generate a revised pitch deck that preserves your original brand colors and design while restructuring the content based on your strategy work.</p>
            
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-slate-900">What gets preserved:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Original background colors and brand theme</li>
                <li>• Visual design elements</li>
              </ul>
              <h4 className="font-medium text-slate-900 mt-3">What gets revised:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Slide structure (10 slides: Title → Problem → Solution → Market → Product → Traction → Team → Business Model → Financials → Ask)</li>
                <li>• Text content and messaging</li>
                <li>• Non-data images and illustrations</li>
              </ul>
            </div>
            
            {!hasDeck && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-800">Upload a pitch deck in your Profile first</span>
              </div>
            )}
            
            <Button onClick={generateRevisedDeck} disabled={generatingDeck || !hasDeck} className="btn-secondary gap-2">
              {generatingDeck ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {generatingDeck ? "Generating..." : "Generate & Download PPTX"}
            </Button>
          </CardContent>
        </Card>

        <Card className="border border-teal-200 bg-teal-50/30">
          <CardContent className="p-5">
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-teal-600" />Tips</h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>• <strong>One-liner:</strong> Lead with impact. What transformation do you enable?</li>
              <li>• <strong>15s pitch:</strong> Perfect for quick intros at networking events</li>
              <li>• <strong>30s pitch:</strong> Ideal for VC office hours or brief meetings</li>
              <li>• <strong>1min pitch:</strong> Full version for formal investor pitches</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function Messaging() {
  return <AppLayout>{({ user }) => <MessagingContent user={user} />}</AppLayout>;
}
